import { flow } from 'fp-ts/function';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { z } from 'zod';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { FeatureFlagService, LocalizedText, MAX_INPUT_TEXT_LENGTH, validateWithZod } from '@mews/framework';
import { Identifiable } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { CompanyOptions } from '@mews-commander/data-access';
import { MAX_LONG_INPUT_TEXT_LENGTH } from '@mews-commander/constants';
import { hasReadOnlyPrivilegesForCompanyData } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { commanderForm } from '@mews-commander/legacy-form';
import { updateAdditionalInfo } from '../../actions';
import { getAdditionalInfoFormInitialValues } from '../../selectors';
import { AdditionalInfoFormValues } from '../../types';
import { AdditionalInfoFields } from '../Fields';

const AdditionalInfoSchema = z.object({
    contact: z.string().max(MAX_LONG_INPUT_TEXT_LENGTH).nullable(),
    contactPerson: z.string().max(MAX_LONG_INPUT_TEXT_LENGTH).nullable(),
    iata: z.string().max(MAX_INPUT_TEXT_LENGTH).nullable(),
    // using union here to allow both numbers and null values, which NumberInputField sets to "" instead of null
    invoiceDueInterval: z.union([z.number().int().min(0).max(365), z.string()]).nullable(),
    // cannot use nativeEnum since there is no enum, the field uses enumifyFunction which is deprecated
    classifications: z.array(z.string()),
    options: z.array(z.nativeEnum(CompanyOptions)),
    sourceId: z.string().nullable(),
});

interface AdditionalInfoFormProps {
    initialValues: AdditionalInfoFormValues;
    readOnlyForm: boolean;
}

interface OwnProps extends Identifiable {
    form: string;
    featureFlagService: FeatureFlagService;
    strawberryReadOnlyFieldsEnabled: boolean;
}

function getReadOnlyFormState(state: ApplicationState, strawberryReadOnlyFieldsEnabled: boolean): boolean {
    return hasReadOnlyPrivilegesForCompanyData(state) || strawberryReadOnlyFieldsEnabled;
}

export default flow(
    commanderForm({
        enableReinitialize: true,
        // TODO: this should be ideally just validate: validateWithZod(AdditionalInfoSchema), however that breaks Generics and introduces different problems, this form should be rewritten to RHF
        validate: (values, props) => validateWithZod(AdditionalInfoSchema)(values, props),
        submitLabel: <LocalizedText textKey={TextKey.Save} />,
        submitButtonProps: { variant: 'primary' },
    }),
    connect<AdditionalInfoFormProps, {}, OwnProps, ApplicationState>(
        createStructuredSelector({
            initialValues: getAdditionalInfoFormInitialValues,
            readOnlyForm: (state: ApplicationState, ownProps: OwnProps) =>
                getReadOnlyFormState(state, ownProps.strawberryReadOnlyFieldsEnabled),
        }),
        (dispatch: CommanderThunkDispatch, { id }) => ({
            onSubmit: async (values: AdditionalInfoFormValues) => dispatch(updateAdditionalInfo(values, id)),
        })
    )
)(AdditionalInfoFields);
